import { forwardRef, memo, useRef, type ReactNode } from 'react';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { FormHelperText, InputAdornment, InputLabel, type TextFieldProps, Tooltip } from '@mui/material';
import { StyledTextField, Wrapper } from './TextField.styles';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';
import { getIcon } from '../../../utils/common.ts';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';
import type { ViewItem } from '../../../types/catalogViewItem.ts';
import { useIsOverflowing } from '../../../utils/hooks';

export interface Props extends Omit<TextFieldProps, 'variant'> {
  label?: ReactNode;
  suffix?: string;
  className?: string;
  background?: 'transparent' | 'white';
  readOnly?: boolean;
  disableHelperText?: boolean;
  dataTestId?: string;
  helperTextDataTestId?: string;
  formItemUiDefinition?: FormItemUiDefinition | null;
  viewItem?: ViewItem;
  lightError?: boolean;
  disableOverflowTooltip?: boolean;
}

export const TextField = memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        className,
        label,
        readOnly,
        error,
        lightError,
        helperText,
        slotProps,
        suffix = '',
        disableHelperText = false,
        dataTestId,
        helperTextDataTestId,
        value,
        formItemUiDefinition,
        viewItem,
        inputRef: inputRefProp,
        disableOverflowTooltip = false,
        ...props
      },
      ref
    ) => {
      const inputRef = useRef<HTMLInputElement | null>(null);
      const setInputRef = (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof inputRefProp === 'function') {
          inputRefProp(node);
        } else if (inputRefProp) {
          (inputRefProp as MuiTextFieldProps['inputRef'] & { current: HTMLInputElement | null }).current = node;
        }
      };
      const { isOverflowing } = useIsOverflowing(inputRef, value ?? '', 0);

      const styles = getUiDefinitionTextStyles(formItemUiDefinition ?? viewItem?.visibilityResult?.formItemUiDefinition);
      const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition ?? viewItem?.visibilityResult?.formItemUiDefinition);
      const Icon =
        (formItemUiDefinition?.icon ?? viewItem?.visibilityResult?.formItemUiDefinition)
          ? getIcon(formItemUiDefinition?.icon ?? viewItem?.visibilityResult?.formItemUiDefinition?.icon ?? '')
          : null;

      return (
        <Wrapper>
          {!!label && <InputLabel disabled={props.disabled}>{label}</InputLabel>}
          <Tooltip
            title={!disableOverflowTooltip && isOverflowing && value ? value.toString() : ''}
            disableHoverListener={disableOverflowTooltip || !isOverflowing || !value}>
            <StyledTextField
              ref={ref}
              inputRef={setInputRef}
              value={value ?? ''}
              variant="outlined"
              className={className}
              error={error}
              lightError={lightError}
              slotProps={{
                ...slotProps,
                htmlInput: {
                  ...slotProps?.htmlInput,
                  'data-testid': dataTestId ?? null,
                },
                input: (ownerState) => {
                  const inputSlotProps = typeof slotProps?.input === 'function' ? slotProps.input(ownerState) : slotProps?.input;
                  const inputSx = inputSlotProps?.sx;

                  return {
                    ...inputSlotProps,
                    sx: [...(Array.isArray(inputSx) ? inputSx : [inputSx]), styles, boxStyles],
                    readOnly,
                    startAdornment: Icon ? (
                      <InputAdornment position="start">
                        <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
                      </InputAdornment>
                    ) : (
                      (inputSlotProps?.startAdornment ?? null)
                    ),
                    endAdornment: inputSlotProps?.endAdornment ? (
                      inputSlotProps.endAdornment
                    ) : suffix.length ? (
                      <InputAdornment position="end">{suffix}</InputAdornment>
                    ) : null,
                  };
                },
              }}
              {...props}
            />
          </Tooltip>
          {!disableHelperText && helperText && (
            <FormHelperText
              data-testid={helperTextDataTestId}
              error={error}
              variant="filled"
              title={typeof helperText === 'string' ? helperText : undefined}>
              {helperText}
            </FormHelperText>
          )}
        </Wrapper>
      );
    }
  )
);
